import { useState, useRef, useEffect } from "react";
import moment from "moment";
import classNames from "classnames";
import { v4 as uuidv4 } from "uuid";
import { Calendar } from "primereact/calendar";
import Icon from "../Icon";
import DateTimeHelper from "../../../core/helpers/dateTimeHelper";

import inputStyles from "../Inputs/Input.module.css";
import styles from "./DateTime.module.css";

export interface TimeInputProps {
  hourFormat?: "24" | "12";
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  description?: string;
  validationError?: string;
  invalid?: boolean;
  value?: string;
  onChange?: (time?: string, dateWithTime?: Date) => void;
  css?: any;
}

/** Time picker with ability to enter time. Returns time as string. */
export const TimePicker = ({
  hourFormat = "24",
  label = "",
  placeholder = "",
  required = false,
  disabled = false,
  description = "",
  validationError = undefined,
  invalid = false,
  value = "",
  onChange = () => {},
  css,
}: TimeInputProps) => {
  const timeFormat = hourFormat === "24" ? "HH:mm" : "hh:mm a";
  placeholder = placeholder || timeFormat;

  const inputRef = useRef<HTMLInputElement>(null);
  const calendarRef = useRef<Calendar>(null);
  const inputId = uuidv4();

  const [isInputActive, setIsInputActive] = useState(false);
  const [inputData, setInputData] = useState<Date>();

  useEffect(() => {
    // convert passed value to date fro input
    if (value && value !== "") {
      const timeComponents = DateTimeHelper.getTimeComponentsFromTimeString(
        value,
        timeFormat,
      );
      const inputDate = moment(new Date())
        .set({
          hour: timeComponents.hours,
          minutes: timeComponents.minutes,
          seconds: 0,
          milliseconds: 0,
        })
        .toDate();
      setInputData(inputDate);
    } else {
      setInputData(undefined);
    }
  }, [value]);

  return (
    <div
      className={classNames({
        [inputStyles.inputContainer]: true,
        [styles.timePickerContainer]: true,
      })}
    >
      {/* label row */}
      <div className={inputStyles.labelRow}>
        {required && <span className={inputStyles.requiredIcon}>*</span>}
        <label htmlFor={inputId}>{label}</label>
      </div>

      {/* input row */}
      <div
        className={classNames({
          [inputStyles.inputRow]: true,
          [styles.inputValidationError]: invalid,
          [inputStyles.inputRowIconLeft]: true,
          [inputStyles.inputRowIconRight]: true,
        })}
      >
        {/* left icon */}
        <span
          className={classNames({
            [inputStyles.inputIcon]: true,
            [inputStyles.inputIconLeft]: true,
            [inputStyles.inputIconClickable]: false,
            [inputStyles.inputIconDisabled]: disabled,
          })}
          aria-hidden="true"
        >
          <Icon name="clock" size={24} color="grey" />
        </span>

        {/* right icon */}
        <button
          type="button"
          tabIndex={-1}
          className={classNames({
            [inputStyles.inputIcon]: true,
            [inputStyles.inputIconRight]: true,
            [inputStyles.inputIconClickable]: true,
            [inputStyles.inputIconDisabled]: disabled,
          })}
          aria-label="Pick time"
          onClick={(e) => {
            // if modal not PrimeReact then this need to stop submit event
            e.preventDefault();
            isInputActive
              ? calendarRef.current?.hide()
              : calendarRef.current?.show();
          }}
          data-cy="timepicker_dropdown_btn"
        >
          <Icon
            name={isInputActive ? "chevron-up" : "chevron-down"}
            size={16}
            color="grey"
          />
        </button>

        {/* input */}
        <Calendar
          inputId={inputId}
          ref={calendarRef}
          timeOnly
          showTime
          style={css}
          disabled={disabled}
          hourFormat={hourFormat}
          showSeconds={false}
          showMillisec={false}
          placeholder={placeholder}
          value={inputData}
          inputRef={inputRef}
          inputClassName={classNames({
            [inputStyles.inputValidationError]: !!validationError,
          })}
          onChange={(e) => {
            // null means invalid time
            if (!e.value) {
              onChange(undefined, undefined);
            } else {
              const nextDate = e.value;
              const nextTime = moment(nextDate).format(timeFormat);

              onChange(nextTime, nextDate);
              setInputData(e.value);
            }
          }}
          onShow={() => {
            setIsInputActive(true);
            inputRef.current?.focus();
          }}
          onHide={() => setIsInputActive(false)}
          onBlur={() => calendarRef.current?.hide()}
        />
      </div>

      {/* Other */}
      <p className="description">{description}</p>
      {validationError ? (
        <div className={inputStyles.validationError}>{validationError}</div>
      ) : null}
    </div>
  );
};
export default TimePicker;
